import { useSceneEvents } from "@/components/common/scene-events-context";
import { useViewOverlayRef } from "@/hooks/use-view-overlay-ref";
import {
  useCached3DObjectIfReady,
  type PointCloudObject,
} from "@/object-cache";
import {
  setCadAnalysisBounds,
  setCadAnalysisStatus,
  type CadPointCloudAnalysis,
} from "@/store/point-cloud-analysis-tool-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { buildCadReference } from "@/tools/cad-analysis/build-cad-reference";
import { registerCadAnalysisReference } from "@/tools/cad-analysis/cad-analysis-runtime";
import {
  CAD_DISTANCE_SHADER,
  CAD_SCOPE_SHADER,
  type CadDistanceReference,
} from "@/tools/cad-analysis/cad-distance-reference";
import {
  cadSourceRevision,
  isCadReferenceCurrent,
} from "@/tools/cad-analysis/cad-source-revision";
import { isIElementModel3dStream } from "@faro-lotv/ielement-types";
import { AdaptivePointsMaterial } from "@faro-lotv/lotv";
import {
  selectIElement,
  selectIElementWorldMatrix,
} from "@faro-lotv/project-source";
import { useThree } from "@react-three/fiber";
import { useEffect, useState, type JSX } from "react";
import { shallowEqual } from "react-redux";
import { Color, Matrix4, Vector3 } from "three";
import { AnalysisLabel } from "./analysis-label";
import { AnalysisLabelMenu } from "./analysis-label-menu";
import { CadClashReview } from "./cad-clash-review";
import { selectCanReadCAD } from "@/store/subscription-features/subscription-features-selectors";
import {
  CAD_BAND_COLOURS,
  CAD_UNMATCHED_COLOUR,
} from "@/tools/cad-analysis/cad-deviation-colouring";

const PROGRESS_INTERVAL_MS = 150;

/** @returns CAD deviation using the existing adaptive-point colormap and label presentation. */
export function CadAnalysisRenderer({
  pointCloud,
  analysis,
  labelsScale,
}: {
  pointCloud: PointCloudObject;
  analysis: CadPointCloudAnalysis;
  labelsScale?: number;
}): JSX.Element {
  const element = useAppSelector(selectIElement(analysis.cadReference.cadId));
  const cadElement =
    element && isIElementModel3dStream(element) ? element : undefined;
  const cad = useCached3DObjectIfReady(cadElement, "CadAnalysisReference");
  const cadPose = useAppSelector(
    selectIElementWorldMatrix(analysis.cadReference.cadId),
    shallowEqual,
  );
  const cloudPose = useAppSelector(
    selectIElementWorldMatrix(analysis.parentId),
    shallowEqual,
  );
  const budget = useAppSelector((state) => state.cad.defaultMaxByteToLoad);
  const canReadCad = useAppSelector(selectCanReadCAD);
  const attempt = useAppSelector(
    (state) => state.pointCloudAnalysisTool.cadAttempt,
  );
  const dispatch = useAppDispatch();
  const events = useSceneEvents();
  const renderer = useThree((state) => state.gl);
  const [reference, setReference] = useState<CadDistanceReference>();
  const [clashSourceReady, setClashSourceReady] = useState(false);
  const { id, cadReference } = analysis;
  const deviationRun = useAppSelector(
    (state) => state.pointCloudAnalysisTool.execution?.[id]?.deviation ?? 1,
  );
  useEffect(() => {
    const controller = new AbortController();
    let owned: CadDistanceReference | undefined;
    let unregister: (() => void) | undefined;
    let lastProgress = -Infinity;
    let lastStage: "geometry" | "bvh" | undefined;
    setReference(undefined);
    setClashSourceReady(false);
    function status(
      phase: "idle" | "loading" | "ready" | "error",
      message?: string,
      stage?: "geometry" | "bvh",
    ): void {
      const now = performance.now();
      if (
        phase === "loading" &&
        stage === lastStage &&
        now - lastProgress < PROGRESS_INTERVAL_MS
      ) {
        return;
      }
      lastProgress = now;
      lastStage = stage;
      if (!controller.signal.aborted) {
        dispatch(setCadAnalysisStatus({ analysisId: id, phase, message }));
      }
    }
    status("loading", "Loading CAD reference");
    async function load(): Promise<void> {
      if (!canReadCad) {
        throw new Error("CAD viewing access is required for this comparison.");
      }
      if (!cadElement) {
        throw new Error("The reference CAD model is no longer available.");
      }
      if (cad instanceof Error) throw cad;
      if (!cad) return;
      cad.updateMatrix();
      pointCloud.updateMatrix();
      const cadWorld = new Matrix4().fromArray(cadPose).multiply(cad.matrix);
      const cloudWorld = new Matrix4()
        .fromArray(cloudPose)
        .multiply(pointCloud.matrix);
      const revision = await cadSourceRevision(cadElement);
      controller.signal.throwIfAborted();
      if (
        !isCadReferenceCurrent(cadReference, revision, cadWorld, cloudWorld)
      ) {
        throw new Error(
          "The reference model or alignment changed. Create a new CAD comparison.",
        );
      }
      setClashSourceReady(true);
      if (!deviationRun) {
        status("idle");
        return;
      }
      owned = await buildCadReference(
        cad,
        cadReference.all ? undefined : cadReference.objectIds,
        renderer,
        budget,
        controller.signal,
        (message, stage) => status("loading", message, stage),
        cadWorld,
        cadReference.clipBox,
      );
      if (controller.signal.aborted) {
        owned.dispose();
        owned = undefined;
        return;
      }
      owned.setPointTransform(cloudWorld);
      unregister = registerCadAnalysisReference(pointCloud, id, owned);
      const bounds = owned.bounds.clone().translate(owned.origin);
      dispatch(
        setCadAnalysisBounds({
          analysisId: id,
          bounds: [bounds.min.toArray(), bounds.max.toArray()],
        }),
      );
      setReference(owned);
      status("ready");
    }
    void load().catch((error: unknown) =>
      status(
        "error",
        error instanceof Error ? error.message : "CAD comparison failed",
      ),
    );
    return () => {
      controller.abort();
      unregister?.();
      if (pointCloud.material instanceof AdaptivePointsMaterial) {
        pointCloud.material.setColormap(undefined);
      }
      owned?.dispose();
      events.invalidatePointCloudScene.emit();
    };
  }, [
    id,
    cadReference,
    cad,
    cadElement,
    cadPose,
    cloudPose,
    pointCloud,
    renderer,
    budget,
    canReadCad,
    attempt,
    deviationRun,
    dispatch,
    events,
  ]);
  useEffect(() => {
    if (
      !reference ||
      !(pointCloud.material instanceof AdaptivePointsMaterial)
    ) {
      return;
    }
    const { material } = pointCloud;
    const colouring = analysis.deviationColouring;
    material.setColormap({
      distanceShader: CAD_DISTANCE_SHADER,
      distanceScopeShader: CAD_SCOPE_SHADER,
      bands:
        colouring?.mode === "bands"
          ? {
              tolerance: analysis.tolerance,
              outerLimit: colouring.outerBandLimit,
              colors: [
                new Color(CAD_BAND_COLOURS[0]),
                new Color(CAD_BAND_COLOURS[1]),
                new Color(CAD_BAND_COLOURS[2]),
                new Color(CAD_BAND_COLOURS[3]),
                new Color(CAD_BAND_COLOURS[4]),
              ],
            }
          : undefined,
      unmatchedColor:
        colouring?.unmatched === "grey"
          ? new Color(CAD_UNMATCHED_COLOUR)
          : undefined,
      distanceUniforms: reference.uniforms,
      colormap: analysis.colormap.map(({ value, color }) => ({
        value,
        color: new Color(color),
      })),
      minDeviation: -analysis.tolerance,
      maxDeviation: analysis.tolerance,
      colorizationDistance: analysis.searchDistance,
    });
    events.invalidatePointCloudScene.emit();
    return () => {
      material.setColormap(undefined);
      events.invalidatePointCloudScene.emit();
    };
  }, [
    reference,
    analysis.colormap,
    analysis.tolerance,
    analysis.searchDistance,
    analysis.deviationColouring,
    pointCloud,
    events,
  ]);
  return (
    <>
      {clashSourceReady && cad && !(cad instanceof Error) && (
        <CadClashReview
          cad={cad}
          cloud={pointCloud}
          analysis={analysis}
          budget={budget}
        />
      )}
      <CadAnalysisLabels
        pointCloud={pointCloud}
        analysis={analysis}
        reference={reference}
        labelsScale={labelsScale}
      />
    </>
  );
}

/** @returns Read-only CAD distance labels, also used over the existing report screenshot. */
export function CadAnalysisLabels({
  analysis,
  reference,
  labelsScale,
}: {
  pointCloud: PointCloudObject;
  analysis: CadPointCloudAnalysis;
  reference?: CadDistanceReference;
  labelsScale?: number;
}): JSX.Element {
  const parent = useViewOverlayRef();
  const [menu, setMenu] = useState<{ id: string; position: Vector3 }>();
  return (
    <>
      {reference &&
        analysis.labels.map((label) => (
          <AnalysisLabel
            key={label.id}
            label={label}
            labelsScale={labelsScale}
            deviation={reference.distance(
              new Vector3().fromArray(label.position),
              analysis.searchDistance,
            )}
            parentRef={parent}
            openContextMenu={(id, position) => setMenu({ id, position })}
          />
        ))}
      {menu && (
        <AnalysisLabelMenu
          analysisId={analysis.id}
          labelId={menu.id}
          position={menu.position}
          parentRef={parent}
          closeMenu={() => setMenu(undefined)}
        />
      )}
    </>
  );
}
